import { buildDemoDiary, TRADES } from '../demo/diaryDemo';

/**
 * Temporary colleague columns for a brand new salon.
 *
 * A one column diary teaches nothing: the owner cannot see what a busy day
 * looks like, or what another stylist's column does. So while onboarding is
 * running we draw two extra columns beside theirs, with a few bookings in
 * the trade they signed up as.
 *
 * Nothing is created. No staff record, no invite, no change to the plan, and
 * so no charge. The columns are drawn from the same generator as the staging
 * diary demo and live only in the browser, so switching the flag off removes
 * every trace of them.
 */

const KEY = 'beu:onboarding-demo-team';

export const isDemoTeamOn = () => { try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; } };
export const setDemoTeam = (on, trade, palette) => {
  try {
    if (on) localStorage.setItem(KEY, '1'); else localStorage.removeItem(KEY);
    if (trade) localStorage.setItem(`${KEY}:trade`, trade);
    if (palette) localStorage.setItem(`${KEY}:palette`, palette);
    window.dispatchEvent(new CustomEvent('beu:demo-team-changed'));
  } catch (e) { /* storage unavailable */ }
};
// The palette picked in Bailey's welcome, so the columns keep the owner's vibe.
const demoPalette = () => {
  try { return localStorage.getItem(`${KEY}:palette`) || 'Coastal'; } catch (e) { return 'Coastal'; }
};
const demoTrade = () => {
  try { return localStorage.getItem(`${KEY}:trade`) || 'salon'; } catch (e) { return 'salon'; }
};

/** Business type at signup to the closest demo trade. */
export function tradeForBusinessType(businessType) {
  if (businessType === 'barber') return 'barber';
  if (businessType === 'beauty_salon' || businessType === 'nail_salon' || businessType === 'spa') return 'beauty';
  return 'salon';
}

export const demoTeamNames = () => {
  const cfg = TRADES[demoTrade()] || TRADES.salon;
  return cfg.staff.slice(1, 3).map((s) => s.name.split(' ')[0]);
};

/**
 * Adds the two colleague columns to a diary API response.
 * Returns the response unchanged when the flag is off.
 */
export function withDemoTeam(diaryData, dateStr) {
  if (!isDemoTeamOn() || !diaryData || !diaryData.staff) return diaryData;
  try {
    const demo = buildDemoDiary({
      startDate: dateStr,
      endDate: dateStr,
      override: { trade: demoTrade(), palette: demoPalette(), density: 'steady' },
    });
    // Two colleagues, never the first entry: that slot stands for the owner,
    // who already has their own real column.
    const colleagues = (demo.staff || []).slice(1, 3).map((s) => ({ ...s, isOnboardingDemo: true }));
    if (!colleagues.length) return diaryData;

    const dates = { ...(diaryData.dates || {}) };
    const day = { ...(dates[dateStr] || {}) };
    colleagues.forEach((s) => {
      const generated = (demo.dates && demo.dates[dateStr] && demo.dates[dateStr][s.id]) || null;
      day[s.id] = generated || { isWorking: false, rota: null, bookings: [], events: [], holidays: [] };
    });
    dates[dateStr] = day;

    return {
      ...diaryData,
      staff: [...diaryData.staff, ...colleagues],
      dates,
      staffOrder: [...(diaryData.staffOrder || diaryData.staff.map((s) => s.id)), ...colleagues.map((s) => s.id)],
      hasOnboardingDemo: true,
    };
  } catch (e) {
    return diaryData;
  }
}

/** True when an id belongs to one of the temporary columns. */
export const isDemoStaffId = (id) => typeof id === 'string' && id.indexOf('demo-staff-') === 0;
